import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser"; // Import from correct package
import ChatInterface from '@features/chat/ChatInterface';
import ExperienceManager from '@features/experience/ExperienceManager';
import ProfileManager from '@features/profile/ProfileManager';
import NavMenu from '@layout/nav/NavMenu';
import DashboardView from '@features/dashboard/DashboardView';
import JobApplicationForm from '@features/jobs/JobApplicationForm';
import CVUpload from '@features/cv/CVUpload';
import { useApiOperations } from '@utils/api-functions';
import monitoringService from '@services/monitoring';
import { loginRequest } from '@config/auth'; // Import loginRequest
import { debug } from '@utils/debug'; // Import debug utility

/**
 * Layout wrapper for authenticated pages, including the navigation menu.
 * Adjusts main content margin based on menu visibility.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The child components to render within the layout.
 * @returns {JSX.Element} The layout component.
 */
const AuthenticatedLayout = ({ children }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  
  const handleMenuVisibilityChange = (isVisible) => {
    setIsMenuVisible(isVisible);
  };
  
  return (
    <div className="min-h-screen bg-huntd-light">
      <NavMenu 
        onMenuVisibilityChange={handleMenuVisibilityChange}
      />
      <div className={`p-6 pt-16 transition-all duration-300 ${isMenuVisible ? 'md:ml-52' : ''}`}>
        {children}
      </div>
    </div>
  );
};

AuthenticatedLayout.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Main application content component.
 * Handles routing, authentication state, profile completion checks,
 * monitoring integration, and renders appropriate views.
 * @returns {JSX.Element|null} The rendered application content or loading/error states.
 */
const AppContent = () => {
  const location = useLocation();
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0]; // Assuming single account scenario

  const [profileComplete, setProfileComplete] = useState(null);
  const [profileCheckError, setProfileCheckError] = useState(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);

  const { getProfileStatus } = useApiOperations(); // Keep only needed operations

  /**
   * Callback function triggered when CVUpload component completes.
   * Currently logs the result. Future implementation might refresh data or notify other components.
   * @param {object} result - The result object from the upload process (e.g., { success: boolean }).
   */
  const handleCVUploadComplete = useCallback((result) => {
    // TODO(developer, 2025-07-04): How should AdminPanel be notified to refresh after upload?
    // Option 1: Event bus
    // Option 2: Lift state slightly (e.g., to AuthenticatedLayout)
    // Option 3: Pass a refetch function down from AdminPanel (complex prop drilling)
    // For now, let's leave this - AdminPanel refetches on mount/auth change.
    // A manual refresh might be needed by the user after upload for now.
    debug('app:cv', 'Upload complete, AdminPanel data might be stale.', result);
  }, []);

  // --- Profile Check Logic ---
  /**
   * Fetches the user's profile completion status from the API.
   * Updates component state based on the result or errors encountered.
   * Avoids running if MSAL interaction is in progress or already checking.
   */
  const checkProfile = useCallback(async () => {
    // Check if authenticated and not already checking
    if (!account || isCheckingProfile || inProgress !== InteractionStatus.None) {
      return;
    }
    setIsCheckingProfile(true);
    try {
      const data = await getProfileStatus();
      setProfileComplete(data.complete);
      setProfileCheckError(null);
    } catch (error) {
      debug('app:profile:error', 'Failed to check profile status:', error);
      setProfileCheckError(error);

      if (error.type === 'auth_required' || error.type === 'auth_failed') {
        setProfileComplete(false);
      } else if (error.type !== 'auth_loading') {
        setProfileComplete(false);
      }
    } finally {
      setIsCheckingProfile(false);
    }
  }, [account, isCheckingProfile, getProfileStatus, inProgress]);

  // Trigger profile check when authentication completes and interaction is done
  useEffect(() => {
    if (inProgress === InteractionStatus.None && account && profileComplete === null && !isCheckingProfile) {
      checkProfile();
    }
  }, [inProgress, account, checkProfile, profileComplete, isCheckingProfile]);

  // --- Monitoring ---
  useEffect(() => {
    if (inProgress === InteractionStatus.None) { // Track page view only when auth is idle
      const pageName = location.pathname === '/' ? 'Home' :
                      location.pathname.charAt(1).toUpperCase() +
                      location.pathname.slice(2).replace(/\//g, '-');
      monitoringService.trackPageView(pageName, {
        path: location.pathname,
        authenticated: !!account // Use account from useMsal
      });
    }
  }, [location, inProgress, account]); // Depend on inProgress and account

  // Set user context for monitoring
  useEffect(() => {
    if (account) {
      monitoringService.setAuthenticatedUserContext(account);
    } else if (inProgress === InteractionStatus.None) { // Clear only when idle and no account
      monitoringService.clearAuthenticatedUserContext();
    }
  }, [account, inProgress]);

  // --- Event Handlers ---
  /**
   * Initiates the MSAL login popup flow.
   * Logs errors if the login process fails.
   */
  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch(e => {
      debug('app:auth:error', 'Login failed:', e);
      // Optionally handle specific errors (e.g., user cancelled)
    });
  };

  // --- Render Logic ---

  // Show spinner during MSAL startup, redirect handling, or profile check
  if (inProgress !== InteractionStatus.None || (account && isCheckingProfile)) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-huntd-info"></div>
        <span className="ml-3 text-huntd-secondary">
          {inProgress !== InteractionStatus.None ? 'Checking authentication...' : 'Loading profile...'}
        </span>
      </div>
    );
  }

  // Show error if profile check failed (and we are authenticated)
  if (account && profileCheckError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-600">
          Error checking profile status: {profileCheckError.message || 'Unknown error'}. Please try refreshing.
        </div>
      </div>
    );
  }

return (
  <>
    <UnauthenticatedTemplate>
      {/* Render login prompt for any path when unauthenticated */}
      <div className="flex flex-col items-center justify-center min-h-screen bg-huntd-light">
        <img src="/huntd_logo.png" alt="Huntd Logo" className="w-64 mb-12 ml-6" /> {/* Removed h-32, increased width & margin, reduced left margin */}
        <p className="text-huntd-secondary mb-8">Please sign in to continue.</p>
        <button
          onClick={handleLogin}
          className="px-6 py-2 bg-huntd-success text-white rounded hover:bg-huntd-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-huntd-success transition duration-150 ease-in-out"
        >
          Sign In
        </button>
      </div>
    </UnauthenticatedTemplate>

    <AuthenticatedTemplate>
      {/* Render authenticated routes only when profile check is complete */}
      {profileComplete !== null && (
        <Routes>
          <Route path="/app/*" element={
            <AuthenticatedLayout>
              <Routes>
                {profileComplete === false ? (
                  <>
                    {/* Force to profile page if incomplete */}
                    <Route path="profile" element={<ProfileManager onProfileUpdate={checkProfile} />} />
                    <Route path="*" element={<Navigate to="/app/profile" replace />} />
                  </>
                ) : (
                  <>
                    {/* Allow access to other pages if profile is complete */}
                    <Route path="dashboard" element={<DashboardView />}>
                      <Route path="applications/new" element={<JobApplicationForm />} />
                    </Route>
                    <Route path="chat" element={<ChatInterface />} />
                    <Route path="cv-upload" element={<CVUpload onUploadComplete={handleCVUploadComplete} />} />
                    <Route path="experience" element={<ExperienceManager />} />
                    <Route path="profile" element={<ProfileManager onProfileUpdate={checkProfile} />} />
                    <Route index element={<Navigate to="/app/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
                  </>
                )}
              </Routes>
            </AuthenticatedLayout>
          } />
          {/* Redirect from root or other paths to the correct starting point */}
          <Route path="*" element={<Navigate to={profileComplete === false ? "/app/profile" : "/app/dashboard"} replace />} />
        </Routes>
      )}
    </AuthenticatedTemplate>
  </>
  );
};

/**
 * Root application component.
 * Sets up the main application structure by rendering AppContent.
 * (MsalProvider is now handled in main.jsx).
 * @returns {JSX.Element} The root application component.
 */
const App = () => {
  return (
    // MsalProvider is now in main.jsx
    <AppContent />
  );
};

export default App;
